import { useEffect, useRef, useState } from 'react';
import { toMajor } from '../../contracts/money';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Database,
  Lock,
  Loader2,
  Mail,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  Tag,
  Wallet,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { Stepper } from '../../components/Stepper';
import { OptionCard } from '../../components/OptionCard';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';
import { usePricingPagePackages } from '../../api/catalog';
// NOTE: src/components/CurrencySelector.tsx is being authored by another agent.
// Import will be re-enabled once that file lands. Until then a small inline
// pill below provides equivalent functionality.
// import { CurrencySelector } from '../../components/CurrencySelector';
import type {
  CurrencyCode,
  GatewaySlug,
} from '../../mocks/clientBatch';
import { gatewaysForCurrency, useCurrencies } from '../../api/platform';
import { sendSignupOtp, verifySignupOtp, signupAccount } from '../../api/auth';
import { startCheckout } from '../../api/billing';
import { useSignupAvailability } from '../../api/hooks';
import { useSession } from '../../stores/session';
import { USE_MOCKS } from '../../api/client';

const STEPS = ['Account', 'Verify', 'Plan', 'Subdomain & DB', 'Payment'];

/**
 * Static FX map for the mock — production stores explicit per-currency prices
 * against each Package row (see NESTJS_SCHEMA.md `prices`).
 *   BDT 1 = USD 0.0091 = GBP 0.0072 = EUR 0.0085
 */
const FX_FROM_BDT: Record<CurrencyCode, number> = {
  BDT: 1,
  USD: 0.0091,
  GBP: 0.0072,
  EUR: 0.0085,
};

type PayMethod = 'sslcommerz' | 'cash' | 'stripe' | 'paypal';

/** Map our PayMethod radio identifiers to the GatewaySlug values from clientBatch. */
const METHOD_TO_GATEWAY: Record<PayMethod, GatewaySlug> = {
  sslcommerz: 'sslcommerz',
  cash: 'manual_cash',
  stripe: 'stripe',
  paypal: 'paypal',
};
const GATEWAY_TO_METHOD: Record<GatewaySlug, PayMethod> = {
  sslcommerz: 'sslcommerz',
  manual_cash: 'cash',
  stripe: 'stripe',
  paypal: 'paypal',
};

/**
 * Self-signup wizard — §5.5
 * Five steps: Account, Verify, Plan, Subdomain & DB, Payment.
 * Submission redirects to /signup/provisioning.
 */
export function Signup() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { data: currencies = [] } = useCurrencies();
  const { data: pricingPagePackages = [] } = usePricingPagePackages();
  const { data: signupAvail } = useSignupAvailability();
  const [step, setStep] = useState(0);

  // Currency (header pill)
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('BDT');
  const currencyMeta =
    currencies.find((c) => c.code === selectedCurrency) ?? currencies[0];

  // step 0 — Account
  const [bizName, setBizName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // step 1 — Verify (OTP)
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '']);
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(5);
  const [otpResendIn, setOtpResendIn] = useState(60);
  const [otpStatus, setOtpStatus] =
    useState<'idle' | 'verifying' | 'verified' | 'failed' | 'locked'>('idle');
  const otpInputs = useRef<Array<HTMLInputElement | null>>([null, null, null, null]);

  // step 2 — Plan (defaults to the first available package once they load)
  const [plan, setPlan] = useState<string>('');
  const [trial, setTrial] = useState(true);

  // step 3 — Subdomain & DB
  const [subdomain, setSubdomain] = useState('');
  const [db, setDb] = useState<'shared' | 'managed' | 'own'>('shared');

  // step 3 — BYOD (bring your own MySQL) connection form
  const [byodHost, setByodHost] = useState('');
  const [byodPort, setByodPort] = useState('3306');
  const [byodDatabase, setByodDatabase] = useState('');
  const [byodUsername, setByodUsername] = useState('');
  const [byodPassword, setByodPassword] = useState('');
  const [byodRequireTls, setByodRequireTls] = useState(true);
  const [byodTest, setByodTest] = useState<{
    state: 'idle' | 'testing' | 'success' | 'failed';
    message?: string;
  }>({ state: 'idle' });

  // step 4 — Payment
  const [payMethod, setPayMethod] = useState<PayMethod>('sslcommerz');
  const [coupon, setCoupon] = useState('');
  const [couponApplied, setCouponApplied] = useState<string | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);

  // Currency pill dropdown
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyPillRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!currencyOpen) return;
    function onDocClick(e: MouseEvent) {
      if (
        currencyPillRef.current &&
        !currencyPillRef.current.contains(e.target as Node)
      ) {
        setCurrencyOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [currencyOpen]);

  // When currency changes, snap payMethod to the first gateway available for it.
  useEffect(() => {
    const allowed = gatewaysForCurrency(selectedCurrency);
    if (allowed.length === 0) return;
    const currentSlug = METHOD_TO_GATEWAY[payMethod];
    const stillAllowed = allowed.some((g) => g.slug === currentSlug);
    if (!stillAllowed) {
      const first = allowed[0]!;
      setPayMethod(GATEWAY_TO_METHOD[first.slug]);
    }
  }, [selectedCurrency, payMethod]);

  // Pre-select the first available package once the catalog loads.
  useEffect(() => {
    if (plan) return;
    const first = pricingPagePackages[0];
    if (first) setPlan(first.id);
  }, [pricingPagePackages, plan]);

  // Send the verification code the first time we land on the Verify step.
  useEffect(() => {
    if (step !== 1 || otpSent) return;
    setOtpSent(true);
    void sendSignupOtp(email, mobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // OTP resend countdown — only ticks while we're on the Verify step.
  useEffect(() => {
    if (step !== 1) return;
    if (otpResendIn <= 0) return;
    const t = setInterval(() => {
      setOtpResendIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [step, otpResendIn]);

  // OTP auto-verify when 4 digits are filled.
  useEffect(() => {
    if (step !== 1) return;
    if (otpStatus === 'locked' || otpStatus === 'verified') return;
    const code = otpDigits.join('');
    if (code.length !== 4) return;
    if (!/^\d{4}$/.test(code)) return;
    setOtpStatus('verifying');
    const t = setTimeout(() => {
      verifySignupOtp(email, code).then((ok) => {
        if (ok) {
          setOtpStatus('verified');
          // small pause so the user sees the success state, then advance
          setTimeout(() => setStep(2), 350);
        } else {
          const newAttempts = otpAttemptsLeft - 1;
          setOtpAttemptsLeft(newAttempts);
          setOtpDigits(['', '', '', '']);
          if (newAttempts <= 0) {
            setOtpStatus('locked');
          } else {
            setOtpStatus('failed');
            // refocus first box for retry
            setTimeout(() => otpInputs.current[0]?.focus(), 0);
          }
        }
      });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpDigits, step]);

  function setOtpDigit(i: number, raw: string) {
    // Sanitize: keep only digits; take last char if multiple
    const digit = raw.replace(/\D/g, '').slice(-1);
    setOtpDigits((prev) => {
      const copy = [...prev];
      copy[i] = digit;
      return copy;
    });
    if (digit && i < 3) {
      otpInputs.current[i + 1]?.focus();
    }
    // Clear failure flag as user starts typing again
    if (otpStatus === 'failed') setOtpStatus('idle');
  }

  function onOtpKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
      otpInputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      otpInputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && i < 3) {
      otpInputs.current[i + 1]?.focus();
    }
  }

  function onOtpPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (text.length === 0) return;
    e.preventDefault();
    const next = ['', '', '', ''];
    for (let i = 0; i < text.length && i < 4; i++) {
      next[i] = text[i]!;
    }
    setOtpDigits(next);
    const focusIndex = Math.min(text.length, 3);
    otpInputs.current[focusIndex]?.focus();
    if (otpStatus === 'failed') setOtpStatus('idle');
  }

  function resendOtp() {
    if (otpResendIn > 0) return;
    void sendSignupOtp(email, mobile);
    setOtpDigits(['', '', '', '']);
    setOtpStatus('idle');
    setOtpResendIn(60);
    setTimeout(() => otpInputs.current[0]?.focus(), 0);
  }

  function requestNewOtpAfterLock() {
    setOtpAttemptsLeft(5);
    setOtpDigits(['', '', '', '']);
    setOtpResendIn(60);
    setOtpStatus('idle');
    setTimeout(() => otpInputs.current[0]?.focus(), 0);
  }

  function next() {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  }
  function back() {
    if (step > 0) setStep((s) => s - 1);
  }

  function applyCoupon() {
    setCouponError(null);
    if (!coupon.trim()) return;
    if (coupon.trim().toUpperCase() === 'EXPIRED-2025') {
      setCouponError('This code has expired. Try another, or continue without one.');
      setCouponApplied(null);
      return;
    }
    setCouponApplied(coupon.trim().toUpperCase());
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < STEPS.length - 1) {
      next();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await signupAccount({
        email, mobile, password, fullName, businessName: bizName, subdomain,
        packageId: selectedPackage?.id ?? '', interval: selectedInterval, currencyCode: selectedCurrency,
        dbMode: db === 'own' ? 'self_hosted' : db, startTrial: trial,
      });
      // Auto-login: the API set the refresh cookie; keep the access token in memory.
      const s = useSession.getState();
      s.setTokens(res.accessToken, null);
      s.setUser({ id: res.user.id, name: res.user.name, email: res.user.email });
      s.setActiveRole('owner');
      s.setActiveBusiness(res.business.id);
      // Pay-now (no trial): hand off to the SSLCommerz hosted checkout.
      if (!res.startTrial) {
        const checkout = await startCheckout({ businessId: res.business.id, priceId: res.priceId });
        if (checkout.gatewayPageURL) {
          window.location.href = checkout.gatewayPageURL;
          return;
        }
      }
      navigate({ to: '/signup/provisioning' });
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err instanceof Error ? err.message : 'Could not create your account. Please try again.');
    }
  }

  const selectedPackage =
    pricingPagePackages.find((p) => p.id === plan) ?? pricingPagePackages[0];
  // Bill on whichever interval the plan actually has (monthly preferred). Reseller
  // catalogs are often yearly-only, so a hard 'month' assumption would 0-price them.
  const selectedInterval: 'month' | 'year' = selectedPackage?.priceMonthlyMinor != null ? 'month' : 'year';
  const intervalUnit = selectedInterval === 'month' ? 'mo' : 'yr';
  const intervalWord = selectedInterval === 'month' ? 'monthly' : 'yearly';
  const planPriceMinor = (selectedInterval === 'month' ? selectedPackage?.priceMonthlyMinor : selectedPackage?.priceYearlyMinor) ?? 0;
  const discount = couponApplied ? Math.round(planPriceMinor * 0.2) : 0;
  const totalToday = trial ? 0 : Math.max(0, planPriceMinor - discount);

  /** First charge date if they take the 7-day trial (computed, not a baked literal). */
  const trialFirstChargeLabel = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(
    undefined,
    { day: 'numeric', month: 'short', year: 'numeric' },
  );

  /** Convert a BDT amount into the currently selected display currency. */
  function toDisplay(amountMinor: number): number {
    return amountMinor * FX_FROM_BDT[selectedCurrency];
  }

  /** "Bring your own MySQL" is gated to Pro+ tiers (Standard ৳700+ in the legacy 7-tier list). */
  const canBringOwnDb = planPriceMinor >= 700;

  // If the user goes back to Plan and picks a lower-tier plan, revert DB choice to managed.
  useEffect(() => {
    if (!canBringOwnDb && db === 'own') {
      setDb('managed');
      setByodTest({ state: 'idle' });
    }
  }, [canBringOwnDb, db]);

  // Re-test required whenever the user edits any connection field after a passing test.
  useEffect(() => {
    if (byodTest.state === 'success') setByodTest({ state: 'idle' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byodHost, byodPort, byodDatabase, byodUsername, byodPassword, byodRequireTls]);

  function testByodConnection() {
    if (
      !byodHost.trim() ||
      !byodDatabase.trim() ||
      !byodUsername.trim() ||
      !byodPassword.trim()
    ) {
      setByodTest({
        state: 'failed',
        message: 'Fill in host, database, username, and password before testing.',
      });
      return;
    }
    setByodTest({ state: 'testing' });
    setTimeout(() => {
      // Mock failure rules so the demo can show both states:
      //   host containing "fail" OR port not 3306 → failure
      const lowerHost = byodHost.toLowerCase();
      if (lowerHost.includes('fail') || lowerHost.includes('error')) {
        setByodTest({
          state: 'failed',
          message: `Connection refused (ECONNREFUSED) at ${byodHost}:${byodPort}. Check that your MySQL server accepts connections on this port from our IP 103.x.x.x.`,
        });
        return;
      }
      if (byodPort !== '3306' && Number.isNaN(Number(byodPort))) {
        setByodTest({
          state: 'failed',
          message: `Invalid port "${byodPort}". Must be a positive integer.`,
        });
        return;
      }
      setByodTest({
        state: 'success',
        message: `Connected. ${byodRequireTls ? 'TLS verified. ' : ''}MySQL 8.0.34 detected. Database "${byodDatabase}" is empty — ready for setup.`,
      });
    }, 1200);
  }

  /**
   * Continue gating:
   *  - On Verify (step 1): require otpStatus === 'verified'.
   *  - On Subdomain & DB (step 3) with BYOD: require successful test.
   */
  const canContinue = (() => {
    // Account (step 0): require all fields + a valid email + the Terms checkbox, so
    // you can't advance with an empty form (and the OTP gets a real address to hit).
    if (step === 0) {
      const emailOk = /\S+@\S+\.\S+/.test(email.trim());
      if (!bizName.trim() || !fullName.trim() || !emailOk || !mobile.trim() || password.length < 8 || !acceptedTerms) return false;
    }
    if (step === 1 && otpStatus !== 'verified') return false;
    if (step === 3 && db === 'own' && byodTest.state !== 'success') return false;
    return true;
  })();

  /** Hide the Continue button on Verify once the user has filled all 4 digits — auto-advance handles it. */
  const otpAllFilled = otpDigits.every((d) => d.length === 1);
  const hideContinueButton = step === 1 && (otpAllFilled || otpStatus === 'verifying' || otpStatus === 'verified');

  // Gateways available for the currently selected currency
  const availableGateways = gatewaysForCurrency(selectedCurrency);

  if (signupAvail && !signupAvail.directSignup) {
    return (
      <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid place-items-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <h1 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">Sign-ups are closed</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            New sign-ups aren't being accepted right now. Please check back later or contact us.
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className="mt-5 text-sm font-medium text-brand dark:text-brand-dm hover:underline"
          >
            Back to sign in
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-700">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </Link>
        <div className="flex items-center gap-3">
          {/* Inline currency pill — replace with <CurrencySelector /> once that component lands. */}
          <div className="relative" ref={currencyPillRef}>
            <button
              type="button"
              onClick={() => setCurrencyOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={currencyOpen}
              className={cn(
                'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-medium',
                'bg-zinc-100 dark:bg-zinc-800',
                'border border-zinc-200 dark:border-zinc-700',
                'text-zinc-700 dark:text-zinc-200',
                'hover:bg-zinc-200/70 dark:hover:bg-zinc-700/70',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:focus-visible:ring-brand-dm',
              )}
            >
              <span aria-hidden="true">{currencyMeta?.flag}</span>
              <span className="tabular-nums">{currencyMeta?.code}</span>
              <ChevronDown className="w-3 h-3 opacity-70" />
            </button>
            {currencyOpen && (
              <ul
                role="listbox"
                aria-label="Currency"
                className={cn(
                  'absolute right-0 mt-1.5 z-20 min-w-[12rem] py-1 rounded-md',
                  'bg-white dark:bg-zinc-900',
                  'border border-zinc-200 dark:border-zinc-700',
                  'shadow-lg',
                )}
              >
                {currencies
                  .filter((c) => c.isActive)
                  .map((c) => {
                    const sel = c.code === selectedCurrency;
                    return (
                      <li key={c.code}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={sel}
                          onClick={() => {
                            setSelectedCurrency(c.code);
                            setCurrencyOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                            'hover:bg-zinc-50 dark:hover:bg-zinc-800',
                            sel
                              ? 'text-zinc-950 dark:text-zinc-50 font-medium'
                              : 'text-zinc-700 dark:text-zinc-200',
                          )}
                        >
                          <span aria-hidden="true">{c.flag}</span>
                          <span className="tabular-nums">{c.code}</span>
                          <span className="text-zinc-500 dark:text-zinc-400 truncate">
                            {c.name}
                          </span>
                          {sel && (
                            <Check className="ml-auto w-3.5 h-3.5 text-brand dark:text-brand-dm" />
                          )}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-brand dark:text-brand-dm hover:underline"
            >
              Log in
            </Link>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8 sm:py-12 max-w-3xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Start your {brand.name} trial
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
            7 days free — no card needed. Cancel any time.
          </p>
        </div>

        <div className="mb-8 flex justify-center">
          <Stepper steps={STEPS} current={step} />
        </div>

        <Card className="p-5 sm:p-8">
          <form onSubmit={onSubmit} className="space-y-6">
            {step === 0 && (
              <div className="space-y-5">
                <Input
                  label="Business name"
                  required
                  value={bizName}
                  onChange={(e) => setBizName(e.target.value)}
                  placeholder="Khan Bakery"
                />
                <Input
                  label="Your full name"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Imran Khan"
                />
                <Input
                  label="Email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@your-shop.com"
                  autoComplete="email"
                />
                <Input
                  label="Mobile (BD)"
                  required
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+8801XXXXXXXXX"
                  autoComplete="tel"
                />
                <Input
                  label="Password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="Use at least 8 characters with letters and numbers"
                  autoComplete="new-password"
                />
                <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  <span>
                    I accept the{' '}
                    <a
                      href="https://itcitysoft.com/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand dark:text-brand-dm hover:underline"
                    >
                      Terms
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://itcitysoft.com/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand dark:text-brand-dm hover:underline"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">
                    Verify your email and phone
                  </h2>
                  <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
                    We sent a 4-digit code to{' '}
                    <span className="font-medium text-zinc-900 dark:text-zinc-100">
                      {email}
                    </span>{' '}
                    and{' '}
                    <span className="font-medium text-zinc-900 dark:text-zinc-100">
                      {mobile}
                    </span>
                    .
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
                    <span className="inline-flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Smartphone className="w-3.5 h-3.5" /> SMS
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  {otpDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        otpInputs.current[i] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={d}
                      onChange={(e) => setOtpDigit(i, e.target.value)}
                      onKeyDown={(e) => onOtpKeyDown(i, e)}
                      onPaste={onOtpPaste}
                      disabled={otpStatus === 'verifying' || otpStatus === 'verified' || otpStatus === 'locked'}
                      aria-label={`OTP digit ${i + 1}`}
                      className={cn(
                        'w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-semibold tabular-nums',
                        'bg-white dark:bg-zinc-900',
                        'border-2 rounded-lg',
                        'text-zinc-950 dark:text-zinc-50',
                        'focus:outline-none focus:ring-2',
                        otpStatus === 'failed'
                          ? 'border-rose-400 dark:border-rose-700 focus:ring-rose-500'
                          : otpStatus === 'verified'
                          ? 'border-emerald-400 dark:border-emerald-700 focus:ring-emerald-500'
                          : 'border-zinc-300 dark:border-zinc-700 focus:ring-brand dark:focus:ring-brand-dm focus:border-brand dark:focus:border-brand-dm',
                        otpStatus === 'locked' && 'opacity-60 cursor-not-allowed',
                      )}
                    />
                  ))}
                </div>

                {USE_MOCKS && (
                  <p className="text-center text-[11px] text-zinc-500 dark:text-zinc-400">
                    (mock: use 1234)
                  </p>
                )}

                {otpStatus === 'verifying' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-zinc-700 dark:text-zinc-200">
                    <Loader2 className="w-4 h-4 animate-spin text-brand dark:text-brand-dm" />
                    Verifying…
                  </div>
                )}

                {otpStatus === 'verified' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="w-4 h-4" />
                    Verified — taking you to the next step…
                  </div>
                )}

                {otpStatus === 'failed' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-rose-600 dark:text-rose-400">
                    <AlertCircle className="w-4 h-4" />
                    Code didn't match — try again. {otpAttemptsLeft}{' '}
                    {otpAttemptsLeft === 1 ? 'attempt' : 'attempts'} left.
                  </div>
                )}

                {otpStatus === 'locked' && (
                  <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 text-center space-y-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-rose-900 dark:text-rose-100">
                      <AlertCircle className="w-4 h-4" />
                      Too many attempts. Request a new code.
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={requestNewOtpAfterLock}
                      iconLeft={<RefreshCw className="w-3.5 h-3.5" />}
                    >
                      Request new code
                    </Button>
                  </div>
                )}

                {otpStatus !== 'locked' && (
                  <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-zinc-200 dark:border-zinc-700">
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="text-xs font-medium text-brand dark:text-brand-dm hover:underline"
                    >
                      Wrong number?
                    </button>
                    <button
                      type="button"
                      onClick={resendOtp}
                      disabled={otpResendIn > 0}
                      className={cn(
                        'inline-flex items-center gap-1.5 text-xs font-medium',
                        otpResendIn > 0
                          ? 'text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
                          : 'text-brand dark:text-brand-dm hover:underline',
                      )}
                    >
                      <RefreshCw className="w-3 h-3" />
                      {otpResendIn > 0
                        ? `Resend code in ${otpResendIn}s`
                        : 'Resend code'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                {pricingPagePackages.map((pkg) => (
                  <OptionCard
                    key={pkg.id}
                    selected={plan === pkg.id}
                    onSelect={() => setPlan(pkg.id)}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-zinc-950 dark:text-zinc-50">
                            {pkg.name}
                          </span>
                          {pkg.badge && (
                            <span className="px-1.5 py-px rounded text-[10px] font-medium bg-brand text-white dark:bg-brand-dm dark:text-zinc-950">
                              {pkg.badge}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                          {pkg.features.slice(0, 3).join(' · ')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {pkg.priceMonthlyMinor !== null ? (
                          <>
                            <Money
                              minor={toDisplay(pkg.priceMonthlyMinor)}
                              currencyCode={selectedCurrency}
                              hideCents={selectedCurrency === 'BDT'}
                              tone="neutral"
                              className="font-semibold text-zinc-950 dark:text-zinc-50"
                            />
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">
                              / month
                            </div>
                          </>
                        ) : (
                          <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                            Custom
                          </span>
                        )}
                      </div>
                    </div>
                  </OptionCard>
                ))}

                <label className="flex items-center gap-2.5 mt-4 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                  <input
                    type="checkbox"
                    checked={trial}
                    onChange={(e) => setTrial(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Start with 7-day free trial (no card needed)
                </label>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                    Subdomain
                  </label>
                  <div className="flex items-stretch rounded-md border border-zinc-300 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 focus-within:ring-2 focus-within:ring-brand dark:focus-within:ring-brand-dm">
                    <input
                      type="text"
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                      className="flex-1 px-3 py-2.5 text-sm bg-transparent text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 focus:outline-none mono"
                      placeholder="khan-bakery"
                    />
                    <span className="px-3 py-2.5 text-sm bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-l border-zinc-200 dark:border-zinc-700 mono whitespace-nowrap">
                      .itcitysoft.com
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> Available
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Database
                  </label>
                  <div className="space-y-3">
                    {brand.allowedDbModes.includes('shared') && (
                      <OptionCard selected={db === 'shared'} onSelect={() => setDb('shared')}>
                        <div className="flex items-start gap-3">
                          <Server className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-zinc-950 dark:text-zinc-50">Shared (recommended)</span>
                              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                Default
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                              We host it on our shared platform. Fastest to start; nothing to manage.
                            </p>
                          </div>
                        </div>
                      </OptionCard>
                    )}
                    {brand.allowedDbModes.includes('managed') && (
                      <OptionCard selected={db === 'managed'} onSelect={() => setDb('managed')}>
                        <div className="flex items-start gap-3">
                          <Server className="w-5 h-5 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="font-medium text-zinc-950 dark:text-zinc-50">Isolated (your own database)</span>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                              A dedicated database on our server, just for you — with phpMyAdmin access.
                            </p>
                          </div>
                        </div>
                      </OptionCard>
                    )}
                    <div
                      className={cn(
                        canBringOwnDb ? '' : 'opacity-60 cursor-not-allowed',
                      )}
                      title={
                        canBringOwnDb
                          ? undefined
                          : `Available on Pro+ plans (Standard ৳700/mo and above). Your selected plan: ${selectedPackage?.name} (৳${toMajor(planPriceMinor)}/${intervalUnit}).`
                      }
                    >
                      <OptionCard
                        selected={db === 'own'}
                        onSelect={() => {
                          if (canBringOwnDb) setDb('own');
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <Database className="w-5 h-5 mt-0.5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-zinc-950 dark:text-zinc-50">
                                My own MySQL
                              </span>
                              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                                Pro+ only
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                              Bring your own MySQL server. We connect over TLS.
                            </p>
                            {!canBringOwnDb && (
                              <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                                Available on Standard ৳700/mo and above.{' '}
                                <button
                                  type="button"
                                  onClick={() => setStep(2)}
                                  className="font-medium text-brand dark:text-brand-dm hover:underline"
                                >
                                  Change plan →
                                </button>
                              </p>
                            )}
                          </div>
                        </div>
                      </OptionCard>
                    </div>

                    {db === 'own' && canBringOwnDb && (
                      <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-800/40 p-4 sm:p-5 space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                            Connect to your MySQL server
                          </h4>
                          <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                            We'll connect from our server (IP 103.x.x.x) over TLS. You don't deploy
                            anything on your end — just open a port and create a database.
                          </p>
                        </div>

                        <div className="grid sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-2">
                            <Input
                              label="MySQL host"
                              required
                              placeholder="db.your-server.com"
                              value={byodHost}
                              onChange={(e) => setByodHost(e.target.value)}
                            />
                          </div>
                          <div>
                            <Input
                              label="Port"
                              placeholder="3306"
                              value={byodPort}
                              onChange={(e) => setByodPort(e.target.value)}
                            />
                          </div>
                        </div>

                        <Input
                          label="Database name"
                          required
                          placeholder="khan_bakery_pos"
                          value={byodDatabase}
                          onChange={(e) => setByodDatabase(e.target.value)}
                          hint="An empty database we'll fill with the POS schema."
                        />

                        <div className="grid sm:grid-cols-2 gap-3">
                          <Input
                            label="Admin user"
                            required
                            placeholder="pos_admin"
                            value={byodUsername}
                            onChange={(e) => setByodUsername(e.target.value)}
                          />
                          <Input
                            label="Admin password"
                            required
                            type="password"
                            placeholder="••••••••••••"
                            value={byodPassword}
                            onChange={(e) => setByodPassword(e.target.value)}
                          />
                        </div>

                        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={byodRequireTls}
                            onChange={(e) => setByodRequireTls(e.target.checked)}
                            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                          />
                          <span>
                            Server requires TLS connection{' '}
                            <span className="text-zinc-500 dark:text-zinc-400">(recommended)</span>
                          </span>
                        </label>

                        <div className="pt-1">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={testByodConnection}
                            loading={byodTest.state === 'testing'}
                            iconLeft={
                              byodTest.state === 'testing' ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <ShieldCheck className="w-3.5 h-3.5" />
                              )
                            }
                          >
                            {byodTest.state === 'testing' ? 'Testing…' : 'Test connection'}
                          </Button>
                        </div>

                        {byodTest.state === 'testing' && (
                          <div className="rounded-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 p-3 flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                            <Loader2 className="w-4 h-4 mt-0.5 animate-spin text-brand dark:text-brand-dm flex-shrink-0" />
                            <span>
                              Connecting to{' '}
                              <span className="mono">
                                {byodHost || '<host>'}:{byodPort || '3306'}
                              </span>
                              …
                            </span>
                          </div>
                        )}

                        {byodTest.state === 'success' && (
                          <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3 space-y-3">
                            <div className="flex items-start gap-2.5 text-sm">
                              <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                              <span className="text-emerald-900 dark:text-emerald-100">
                                {byodTest.message}
                              </span>
                            </div>
                            <div className="text-xs text-emerald-900/80 dark:text-emerald-100/80 pl-6 space-y-1.5">
                              <div className="font-semibold uppercase tracking-wider text-[10px] opacity-70">
                                On Continue, we will:
                              </div>
                              <div className="flex items-start gap-1.5">
                                <Check className="w-3 h-3 mt-1 flex-shrink-0" />
                                <span>Verify the database is empty (or back up if not)</span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <Check className="w-3 h-3 mt-1 flex-shrink-0" />
                                <span>Run the POS schema bundle (114 tables)</span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <Check className="w-3 h-3 mt-1 flex-shrink-0" />
                                <span>
                                  Seed default data (currencies, permissions, invoice scheme)
                                </span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <Check className="w-3 h-3 mt-1 flex-shrink-0" />
                                <span>Encrypt and save your credentials</span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <Check className="w-3 h-3 mt-1 flex-shrink-0" />
                                <span>Email you a downloadable schema bundle for backup</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {byodTest.state === 'failed' && (
                          <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-3 flex items-start gap-2.5 text-sm">
                            <AlertCircle className="w-4 h-4 mt-0.5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                            <span className="text-rose-900 dark:text-rose-100">
                              {byodTest.message}
                            </span>
                          </div>
                        )}

                        <p className="text-[11px] text-zinc-500 dark:text-zinc-400 pt-1 border-t border-zinc-200 dark:border-zinc-700">
                          The Laravel POS itself is never deployed to your server. We only connect
                          to it from ours over TLS. You can revoke our access any time by changing
                          the password.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40 p-4 sm:p-5">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium mb-2">
                    Plan
                  </div>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">
                        {selectedPackage?.name} ({intervalWord})
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400">
                        {trial
                          ? `7-day trial · first charge on ${trialFirstChargeLabel}`
                          : `${intervalWord.charAt(0).toUpperCase()}${intervalWord.slice(1)} subscription`}
                      </div>
                    </div>
                    <Money
                      minor={toDisplay(planPriceMinor)}
                      currencyCode={selectedCurrency}
                      hideCents={selectedCurrency === 'BDT'}
                      tone="neutral"
                      className="font-semibold text-zinc-950 dark:text-zinc-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Have a promo code?
                  </label>
                  <div className="flex items-stretch gap-2">
                    <input
                      type="text"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                      placeholder="EID2026"
                      className={cn(
                        'flex-1 px-3 py-2.5 text-sm rounded-md mono uppercase',
                        'bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50',
                        'border border-zinc-300 dark:border-zinc-700',
                        'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
                      )}
                    />
                    <Button variant="secondary" type="button" onClick={applyCoupon}>
                      Apply
                    </Button>
                  </div>
                  {couponApplied && (
                    <p className="mt-2 text-xs flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                      <Check className="w-3.5 h-3.5" />
                      <span className="font-medium">{couponApplied}</span> applied — 20% off for
                      3 months
                    </p>
                  )}
                  {couponError && (
                    <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                      {couponError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                    Pay with
                  </label>
                  <div className="space-y-3">
                    {availableGateways.map((gw) => {
                      const method = GATEWAY_TO_METHOD[gw.slug];
                      const isPrimary = gw.isPrimaryFor?.includes(selectedCurrency) ?? false;
                      let Icon = CreditCard;
                      let title = gw.displayName;
                      let body = gw.description;
                      if (gw.slug === 'sslcommerz') {
                        Icon = CreditCard;
                        title = 'Card / bKash / Nagad / bank';
                        body = 'Secured by SSLCommerz. BDT only.';
                      } else if (gw.slug === 'manual_cash') {
                        Icon = Tag;
                        title = 'Pay cash to office';
                        body = "You'll get the bank/office details by email.";
                      } else if (gw.slug === 'stripe') {
                        Icon = CreditCard;
                        title = 'Stripe';
                        body = 'International cards. USD, GBP, EUR.';
                      } else if (gw.slug === 'paypal') {
                        Icon = Wallet;
                        title = 'PayPal';
                        body = 'Pay with your PayPal balance or linked card.';
                      }
                      return (
                        <OptionCard
                          key={gw.slug}
                          selected={payMethod === method}
                          onSelect={() => setPayMethod(method)}
                        >
                          <div className="flex items-start gap-3">
                            <Icon
                              className={cn(
                                'w-5 h-5 mt-0.5 flex-shrink-0',
                                payMethod === method
                                  ? 'text-brand dark:text-brand-dm'
                                  : 'text-zinc-500 dark:text-zinc-400',
                              )}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-zinc-950 dark:text-zinc-50">
                                  {title}
                                </span>
                                {isPrimary && (
                                  <span className="px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    Recommended
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
                                {body}
                              </p>
                            </div>
                          </div>
                        </OptionCard>
                      );
                    })}
                  </div>
                </div>

                {/* Order summary */}
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-950 p-4 sm:p-5">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 font-medium mb-3">
                    Order summary
                  </div>
                  <dl className="text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <dt className="text-zinc-700 dark:text-zinc-200">
                        {selectedPackage?.name} plan ({intervalWord})
                      </dt>
                      <dd className="tabular-nums text-zinc-950 dark:text-zinc-50">
                        <Money
                          minor={toDisplay(planPriceMinor)}
                          currencyCode={selectedCurrency}
                          hideCents={selectedCurrency === 'BDT'}
                          tone="neutral"
                        />
                      </dd>
                    </div>
                    {couponApplied && (
                      <div className="flex items-center justify-between">
                        <dt className="text-zinc-700 dark:text-zinc-200">
                          Coupon {couponApplied}
                        </dt>
                        <dd className="tabular-nums text-emerald-700 dark:text-emerald-400">
                          −{' '}
                          <Money
                            minor={toDisplay(discount)}
                            currencyCode={selectedCurrency}
                            hideCents={selectedCurrency === 'BDT'}
                            tone="neutral"
                          />
                        </dd>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
                      <dt>Tax (VAT 15%, inclusive)</dt>
                      <dd className="tabular-nums">
                        (
                        <Money
                          minor={toDisplay(Math.round(planPriceMinor * 0.15))}
                          currencyCode={selectedCurrency}
                          hideCents={selectedCurrency === 'BDT'}
                          tone="neutral"
                        />
                        )
                      </dd>
                    </div>
                    <div className="border-t border-zinc-200 dark:border-zinc-700 pt-2 mt-2 flex items-center justify-between font-semibold text-zinc-950 dark:text-zinc-50">
                      <dt>Total today</dt>
                      <dd className="tabular-nums">
                        <Money
                          minor={toDisplay(totalToday)}
                          currencyCode={selectedCurrency}
                          hideCents={selectedCurrency === 'BDT'}
                          tone="neutral"
                        />
                      </dd>
                    </div>
                  </dl>
                </div>

                <p className="flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {selectedCurrency === 'BDT'
                    ? 'Secured by SSLCommerz · BDT'
                    : `Secured by Stripe / PayPal · ${selectedCurrency}`}
                </p>
              </div>
            )}

            {submitError && step === STEPS.length - 1 && (
              <p className="mt-3 text-sm text-rose-600 dark:text-rose-400" role="alert">{submitError}</p>
            )}

            {/* Footer */}
            <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
              {step > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={back}
                  iconLeft={<ArrowLeft className="w-4 h-4" />}
                >
                  Back
                </Button>
              ) : (
                <span />
              )}

              {!hideContinueButton && (
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={!canContinue}
                  iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
                >
                  {step < STEPS.length - 1
                    ? 'Continue'
                    : trial
                    ? 'Start trial'
                    : 'Create account'}
                </Button>
              )}
            </div>
          </form>
        </Card>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
          <Lock className="w-3 h-3" />
          Encrypted · 7-day free trial · Cancel any time
        </p>
      </main>
    </div>
  );
}
